import React, { FC, useState, useEffect, useCallback } from 'react';
import { Modal, Button, Select, Collapse, Spin, Input, Tooltip, message } from 'antd';
import { RegenerateIcon, EditPencilIcon, CloseIcon } from 'components/Elements/CustomSVGIcon';
import AuthImage from 'components/Elements/AuthImage';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'core/types';
import { AlarmCodeHistoryEntry, UpdateAlarmCodesPayload } from 'core/sites/types';
import { fetchApiAuth } from 'core/utils/api';
import {
    fetchAlarmCodes,
    generateAlarmCode,
    updateAlarmCodes,
    fetchAlarmCodeHistory,
} from 'core/sites/sitesApi';
import './alarmCodesModal.scss';

// eslint-disable-next-line react/require-default-props
const CollapseExpandIcon: FC<{ isActive?: boolean }> = ({ isActive }) => (
    <svg
        width="20"
        height="13"
        viewBox="0 0 22 13"
        fill="none"
        style={{
            transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
        }}>
        <path d="M21 1.5L11 11.5L1 1.5" stroke="#2B2D42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DIGIT_OPTIONS = [
    { value: 4, label: '4 digits' },
    { value: 5, label: '5 digits' },
    { value: 6, label: '6 digits' },
    { value: 7, label: '7 digits' },
    { value: 8, label: '8 digits' },
    { value: 9, label: '9 digits' },
    { value: 10, label: '10 digits' },
];

type CodeType = 'user' | 'manager' | 'engineer';

const CODE_TYPES: CodeType[] = ['user', 'manager', 'engineer'];

const MIN_CODE_LENGTH = 4;
const MAX_CODE_LENGTH = 10;

const CODE_IMAGE_FIELD_MAP: Record<CodeType, keyof AlarmCodeHistoryEntry> = {
    user: 'userCodeImage',
    manager: 'managerCodeImage',
    engineer: 'engineerCodeImage',
};

const EMPTY_CODE_MAP: Record<CodeType, string | null> = { user: null, manager: null, engineer: null };
const EMPTY_MANUAL_MAP: Record<CodeType, string> = { user: '', manager: '', engineer: '' };

const isValidManualCode = (code: string) => new RegExp(`^\\d{${MIN_CODE_LENGTH},${MAX_CODE_LENGTH}}$`).test(code);

interface AlarmCodesModalProps {
    visible: boolean;
    onClose: () => void;
    encryptedId: string;
}

const AlarmCodesModal: FC<AlarmCodesModalProps> = ({
    visible,
    onClose,
    encryptedId,
}) => {
    const dispatch = useDispatch<AppDispatch>();

    // Encrypted codes returned by the generate endpoint, awaiting save
    const [generatedCodes, setGeneratedCodes] = useState<Record<CodeType, string | null>>(EMPTY_CODE_MAP);
    // Plaintext codes typed in manually, awaiting save
    const [manualCodes, setManualCodes] = useState<Record<CodeType, string>>(EMPTY_MANUAL_MAP);
    const [editingCodes, setEditingCodes] = useState<Set<CodeType>>(new Set());
    const [codeImages, setCodeImages] = useState<Record<CodeType, string | null>>(EMPTY_CODE_MAP);
    const [originalCodeImages, setOriginalCodeImages] = useState<Record<CodeType, string | null>>(EMPTY_CODE_MAP);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState<string | null>(null);
    const [changedCodes, setChangedCodes] = useState<Set<CodeType>>(new Set());
    const [history, setHistory] = useState<AlarmCodeHistoryEntry[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    const loadAlarmCodes = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await dispatch(fetchAlarmCodes(encryptedId)).unwrap();
            const images = result.alarmCodes ? {
                user: result.alarmCodes.userCodeImage,
                manager: result.alarmCodes.managerCodeImage,
                engineer: result.alarmCodes.engineerCodeImage,
            } : EMPTY_CODE_MAP;
            setCodeImages(images);
            setOriginalCodeImages(images);
        } catch {
            // Error handled by thunk
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, encryptedId]);

    const resetPendingChanges = () => {
        setGeneratedCodes(EMPTY_CODE_MAP);
        setManualCodes(EMPTY_MANUAL_MAP);
        setEditingCodes(new Set());
        setChangedCodes(new Set());
    };

    useEffect(() => {
        if (visible) {
            loadAlarmCodes();
            resetPendingChanges();
            setHistory([]);
            setHistoryLoaded(false);
        }
    }, [visible, loadAlarmCodes]);

    const markChanged = (codeType: CodeType, hasChanged: boolean) => {
        setChangedCodes((prev) => {
            const next = new Set(prev);
            if (hasChanged) {
                next.add(codeType);
            } else {
                next.delete(codeType);
            }
            return next;
        });
    };

    const handleGenerate = async (codeType: CodeType, digitLength: number) => {
        setIsGenerating(codeType);
        try {
            const result = await dispatch(generateAlarmCode({ encryptedId, digitLength })).unwrap();
            // A generated code replaces anything typed in manually
            setManualCodes((prev) => ({ ...prev, [codeType]: '' }));
            setEditingCodes((prev) => {
                const next = new Set(prev);
                next.delete(codeType);
                return next;
            });
            setGeneratedCodes((prev) => ({ ...prev, [codeType]: result.code }));
            setCodeImages((prev) => ({ ...prev, [codeType]: result.codeImage }));
            markChanged(codeType, true);
        } catch {
            // Error handled by thunk
        } finally {
            setIsGenerating(null);
        }
    };

    const handleManualChange = (codeType: CodeType, value: string) => {
        const digitsOnly = value.replace(/\D/g, '').slice(0, MAX_CODE_LENGTH);
        setManualCodes((prev) => ({ ...prev, [codeType]: digitsOnly }));
        // A manually typed code replaces any generated code that has not been saved yet
        setGeneratedCodes((prev) => ({ ...prev, [codeType]: null }));
        setCodeImages((prev) => ({ ...prev, [codeType]: originalCodeImages[codeType] }));
        markChanged(codeType, digitsOnly !== '');
    };

    const toggleEditing = (codeType: CodeType) => {
        const isEditing = editingCodes.has(codeType);
        setEditingCodes((prev) => {
            const next = new Set(prev);
            if (isEditing) {
                next.delete(codeType);
            } else {
                next.add(codeType);
            }
            return next;
        });

        // Cancelling an edit discards whatever was typed
        if (isEditing) {
            setManualCodes((prev) => ({ ...prev, [codeType]: '' }));
            if (!generatedCodes[codeType]) {
                markChanged(codeType, false);
            }
        }
    };

    const loadHistory = useCallback(async () => {
        setIsLoadingHistory(true);
        try {
            const result = await dispatch(fetchAlarmCodeHistory({ encryptedId })).unwrap();
            setHistory(result.data);
            setHistoryLoaded(true);
        } catch {
            // Error handled by thunk
        } finally {
            setIsLoadingHistory(false);
        }
    }, [dispatch, encryptedId]);

    const buildUpdatePayload = (): UpdateAlarmCodesPayload => {
        const payload: UpdateAlarmCodesPayload = { encryptedId };

        changedCodes.forEach((codeType) => {
            const manualCode = manualCodes[codeType];
            if (manualCode) {
                payload[`${codeType}CodePlain`] = manualCode;
            } else {
                payload[`${codeType}Code`] = generatedCodes[codeType];
            }
        });

        return payload;
    };

    const handleUpdate = async () => {
        if (changedCodes.size === 0) {
            onClose();
            return;
        }

        const invalidCode = Array.from(changedCodes)
            .find((codeType) => manualCodes[codeType] && !isValidManualCode(manualCodes[codeType]));

        if (invalidCode) {
            message.error(`Codes must be between ${MIN_CODE_LENGTH} and ${MAX_CODE_LENGTH} digits.`);
            return;
        }

        setIsSaving(true);
        try {
            const result = await dispatch(updateAlarmCodes(buildUpdatePayload())).unwrap();
            // Saved codes come back rendered as images, so the plaintext is never held on screen
            const images = {
                user: result.alarmCodes.userCodeImage,
                manager: result.alarmCodes.managerCodeImage,
                engineer: result.alarmCodes.engineerCodeImage,
            };
            setCodeImages(images);
            setOriginalCodeImages(images);
            resetPendingChanges();
            if (historyLoaded) {
                loadHistory();
            }
        } catch (error) {
            message.error(typeof error === 'string' ? error : 'Failed to update alarm codes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setCodeImages(EMPTY_CODE_MAP);
        resetPendingChanges();
        setHistory([]);
        setHistoryLoaded(false);
        onClose();
    };

    const handleCollapseChange = (keys: string | string[]) => {
        const activeKeys = Array.isArray(keys) ? keys : [keys];
        if (activeKeys.length > 0 && !historyLoaded) {
            loadHistory();
        }
    };

    const getFilteredHistory = useCallback((codeType: CodeType) => {
        const field = CODE_IMAGE_FIELD_MAP[codeType];
        const savedImage = originalCodeImages[codeType];
        // Sort newest-first to ensure correct comparison order
        const sorted = [...history].sort((a, b) => {
            const dateA = a.updatedAt || '';
            const dateB = b.updatedAt || '';
            return dateB.localeCompare(dateA);
        });
        return sorted.filter((entry, index) => {
            const image = entry[field] as string | null;
            // Skip entries where the code was null (not yet set)
            if (!image) return false;
            // Compare against the saved (DB) value for the first entry,
            // or against the newer history entry for subsequent ones
            const newerEntry = sorted[index - 1];
            const newerImage = index === 0 ? savedImage : ((newerEntry?.[field] as string | null) ?? null);
            return image !== newerImage;
        });
    }, [history, originalCodeImages]);

    const handleDownloadHistory = async () => {
        try {
            const response = await fetchApiAuth({
                method: 'GET',
                url: `/sites/${encryptedId}/alarm-codes/history/download`,
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'alarm-codes-history.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            message.error('Failed to download history');
        }
    };

    const renderHistoryList = (codeType: CodeType) => {
        const field = CODE_IMAGE_FIELD_MAP[codeType];

        if (isLoadingHistory) {
            return (
                <div className="history-loading">
                    <Spin size="small" />
                </div>
            );
        }

        const filteredHistory = getFilteredHistory(codeType);

        if (filteredHistory.length === 0) {
            return <div className="history-empty">No previous codes found.</div>;
        }

        return (
            <>
                <ul className="history-list">
                    {filteredHistory.map((entry) => (
                        <li key={entry.id} className="history-item">
                            <span className="history-code">
                                {(entry[field] as string)
                                    ? <AuthImage src={entry[field] as string} alt="Alarm code" />
                                    : '-'}
                            </span>
                            <span className="history-meta">
                                {entry.updatedAt}
                                {entry.updatedBy && ` - Updated by ${entry.updatedBy}`}
                            </span>
                        </li>
                    ))}
                </ul>
                <button
                    type="button"
                    className="download-link"
                    onClick={() => handleDownloadHistory()}>
                    Download History Log
                </button>
            </>
        );
    };

    const renderCodeColumn = (label: string, codeType: CodeType) => {
        const isEditing = editingCodes.has(codeType);
        const manualCode = manualCodes[codeType];
        const codeImage = codeImages[codeType];
        const showLengthError = manualCode !== '' && !isValidManualCode(manualCode);

        return (
            <div className="code-column">
                <div className="code-label">{label}</div>
                <div className="code-input-row">
                    <div className="code-image-container">
                        {isEditing && (
                            <Input
                                className="code-manual-input"
                                value={manualCode}
                                autoFocus
                                inputMode="numeric"
                                maxLength={MAX_CODE_LENGTH}
                                placeholder={`Enter ${MIN_CODE_LENGTH}-${MAX_CODE_LENGTH} digit code`}
                                aria-label={`${label} manual entry`}
                                status={showLengthError ? 'error' : ''}
                                onChange={(e) => handleManualChange(codeType, e.target.value)} />
                        )}
                        {!isEditing && (codeImage
                            ? <AuthImage src={codeImage} alt={label} className="code-image" />
                            : <span className="no-code-placeholder">No code set</span>)}
                    </div>
                    <Tooltip title={isEditing ? 'Cancel manual entry' : 'Enter code manually'}>
                        <button
                            type="button"
                            className="manual-edit-button"
                            aria-label={isEditing ? `Cancel manual entry for ${label}` : `Enter ${label} manually`}
                            onClick={() => toggleEditing(codeType)}>
                            {isEditing ? <CloseIcon w={12} h={12} /> : <EditPencilIcon w={16} h={16} />}
                        </button>
                    </Tooltip>
                    <Tooltip title="Generate code">
                        <Select
                            placeholder={<span className={isGenerating === codeType ? 'regenerate-spin' : ''}><RegenerateIcon w={18} h={16} /></span>}
                            className="digit-select"
                            options={DIGIT_OPTIONS}
                            value={null}
                            popupMatchSelectWidth={false}
                            onChange={(value: number) => handleGenerate(codeType, value)} />
                    </Tooltip>
                </div>
                {showLengthError && (
                    <div className="code-error">
                        {`Code must be between ${MIN_CODE_LENGTH} and ${MAX_CODE_LENGTH} digits.`}
                    </div>
                )}
                <div className="code-history">
                    <Collapse
                        expandIconPosition="end"
                        expandIcon={CollapseExpandIcon}
                        onChange={handleCollapseChange}
                        items={[{
                            key: `history-${codeType}`,
                            label: 'View Code History',
                            children: renderHistoryList(codeType),
                        }]} />
                </div>
            </div>
        );
    };

    const hasInvalidManualCode = CODE_TYPES
        .some((codeType) => manualCodes[codeType] !== '' && !isValidManualCode(manualCodes[codeType]));

    return (
        <Modal
            title="Alarm Codes"
            open={visible}
            onCancel={handleClose}
            footer={null}
            centered
            width={1200}
            wrapClassName="alarm-codes-modal">
            {isLoading ? (
                <div className="modal-loading">
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    <div className="codes-grid">
                        {renderCodeColumn('User Code', 'user')}
                        {renderCodeColumn('Manager Code', 'manager')}
                        {renderCodeColumn('Engineer Code', 'engineer')}
                    </div>

                    <div className="modal-footer-buttons">
                        <Button
                            type="primary"
                            className="update-button"
                            loading={isSaving}
                            disabled={hasInvalidManualCode}
                            onClick={handleUpdate}>
                            Update
                        </Button>
                        <Button
                            className="close-button"
                            onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default AlarmCodesModal;
